import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Paper,
    Avatar,
    Divider,
    Stack,
    IconButton,
    Button
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    SmartToy as SmartToyIcon,
    Share as ShareIcon,
    AutoAwesome as AutoAwesomeIcon,
    Security as SecurityIcon,
    Speed as SpeedIcon,
    Language as LanguageIcon,
    Assessment as AssessmentIcon,
    Hub as IntegrationIcon,
    Chat as ChatIcon,
    Person as PersonIcon,
    Description as DescriptionIcon,
    Campaign as CampaignIcon,
    Business as BusinessIcon,
    School as SchoolIcon,
    Support as SupportIcon,
    ShoppingCart as ShoppingCartIcon,
    ArrowBackIosNew as ArrowBackIosNewIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

// Modern autoplay carousel using public/assets images
function GalleryCarousel() {
    const images = [
        'dashboard.png',
        'botmanagement.png',
        'botedit.png',
        'botedit2.png',
        'boteducation.png',
        'botsettings.png',
        'botsettings2.png',
        'botanalytics.png',
        'botintegration1.png',
        'botintegration2.png',
        'botintegration3.png',
        'widget1.png',
        'apikey.png',
        'usermanagement.png',
        'usermanagement2.png',
        'analytic.png',
        'analyticdocs.png',
        'settingscompany.png',

    ].map((f) => `${process.env.PUBLIC_URL}/assets/${f}`);

    const [index, setIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const touchStartX = useRef(null);

    const length = images.length;
    const prev = () => setIndex((i) => (i - 1 + length) % length);
    const next = () => setIndex((i) => (i + 1) % length);

    useEffect(() => {
        if (length === 0) return;
        const id = setInterval(() => {
            if (!isHovered) {
                // advance without depending on next() to satisfy hooks exhaustive-deps
                setIndex((i) => (i + 1) % length);
            }
        }, 4000);
        return () => clearInterval(id);
    }, [isHovered, length]);

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        const startX = touchStartX.current;
        const endX = e.changedTouches[0].clientX;
        if (startX == null) return;
        const delta = endX - startX;
        if (Math.abs(delta) > 50) {
            if (delta > 0) prev(); else next();
        }
        touchStartX.current = null;
    };

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            sx={{ position: 'relative' }}
        >
            {/* Viewport */}
            <Box sx={{ overflow: 'hidden', borderRadius: 3, boxShadow: 3 }}>
                {/* Slides container */}
                <Box
                    component={motion.div}
                    animate={{ x: `-${index * 100}%` }}
                    transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                    sx={{ display: 'flex', width: '100%' }}
                >
                    {images.map((src, i) => (
                        <Box key={i} sx={{ minWidth: '100%', position: 'relative' }}>
                            <Box sx={{ position: 'relative', pt: '56.25%' /* 16:9 */ }}>
                                <Box
                                    component="img"
                                    src={src}
                                    alt={`Ekran görüntüsü ${i + 1}`}
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        bgcolor: '#f5f5f5'
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Controls */}
            <IconButton
                aria-label="Önceki"
                onClick={prev}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 8,
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                }}
            >
                <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
                aria-label="Sonraki"
                onClick={next}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                }}
            >
                <ArrowForwardIosIcon />
            </IconButton>

            {/* Indicators */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                {images.map((_, i) => (
                    <Box
                        key={i}
                        onClick={() => setIndex(i)}
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: i === index ? 'primary.main' : 'grey.400',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );
}

const ChatCAT = () => {
    // Ana özellikler
    const mainFeatures = [
        {
            icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
            title: 'Akıllı Chatbot Yönetimi',
            description: 'İstediğiniz kadar chatbot oluşturun. İsim, avatar, karşılama mesajı ve konuşma tarzını markanıza özel olarak dakikalar içinde kişiselleştirin.',
            color: '#1e3c72'
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
            title: 'Gelişmiş Veri Öğrenme',
            description: 'Teknik bilgiye ihtiyaç duymadan; Web sitesi linki, PDF/Word dosyaları veya API entegrasyonu ile botunuzun tüm verilerinizi saniyeler içinde öğrenmesini sağlayın.',
            color: '#1e3c72'
        },
        {
            icon: <IntegrationIcon sx={{ fontSize: 40 }} />,
            title: 'Kurumsal Entegrasyonlar',
            description: 'SharePoint ve diğer kurumsal bulut sistemleriyle tam senkronizasyon. Dosyalarınız güncellendikçe botunuzun bilgisini otomatik olarak tazeleyin.',
            color: '#1e3c72'
        },
        {
            icon: <PersonIcon sx={{ fontSize: 40 }} />,
            title: 'Ekipler İçin Gelişmiş Roller',
            description: 'Birden fazla kullanıcı ve ekip yönetimi. Yönetici, editör ve görüntüleyici rolleriyle güvenli ve kontrollü bir çalışma ortamı kurgulayın.',
            color: '#1e3c72'
        },
        {
            icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
            title: 'Raporlama ve İyileştirme',
            description: 'Sohbet analizleri, kullanıcı geri bildirimleri ve kullanım raporları ile botunuzun performansını izleyin; sürekli iyileştirme ile deneyimi mükemmelleştirin.',
            color: '#1e3c72'
        },
        {
            icon: <SpeedIcon sx={{ fontSize: 40 }} />,
            title: 'Hızlı Web Entegrasyonu',
            description: 'Chatbot\'unuzu web sitenize tek satır kod ile ekleyin. Tüm cihazlarda kusursuz çalışan, özelleştirilebilir bir widget deneyimi sunun.',
            color: '#1e3c72'
        }
    ];
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Kullanım alanları
    const useCases = [
        {
            icon: <SupportIcon />,
            title: 'Müşteri Desteği',
            description: '7/24 otomatik yanıt sistemi'
        },
        {
            icon: <DescriptionIcon />,
            title: 'Ürün Bilgilendirme',
            description: 'Belgelerinizden anında bilgi paylaşımı'
        },
        {
            icon: <CampaignIcon />,
            title: 'Potansiyel Müşteri Toplama',
            description: 'Otomatik form ve yönlendirme'
        },
        {
            icon: <BusinessIcon />,
            title: 'Şirket İçi Bilgi Paylaşımı',
            description: 'Personel destek asistanı'
        },
        {
            icon: <ShareIcon />,
            title: 'SharePoint Entegrasyonu',
            description: 'Kurumsal belge erişimi'
        },
        {
            icon: <ShoppingCartIcon />,
            title: 'E-Ticaret Asistanı',
            description: 'Ürün önerileri, sipariş bilgisi'
        },
        {
            icon: <SchoolIcon />,
            title: 'Eğitim ve Onboarding',
            description: 'Yeni çalışan veya kullanıcı eğitimi'
        },
        {
            icon: <AssessmentIcon />,
            title: 'Finansal Danışmanlık',
            description: 'Finansal işlemler ve bilgilendirme'
        },
        {
            icon: <PersonIcon />,
            title: 'İnsan Kaynakları',
            description: 'İK süreçleri ve personel yönetimi'
        },
        {
            icon: <ChatIcon />,
            title: 'Satış ve Pazarlama',
            description: 'Ürün tanıtımı ve satış desteği'
        },
        {
            icon: <AutoAwesomeIcon />,
            title: 'Teknik Destek',
            description: 'IT ve teknik problemlere hızlı çözüm'
        },
        {
            icon: <LanguageIcon />,
            title: 'Çok Dilli Destek',
            description: 'Farklı dillerde müşteri hizmeti'
        }
    ];

    // Neden Chat-CAT?
    const whyChatCAT = [
        { icon: <SpeedIcon />, title: '5 Dakikada Kurulum', text: 'API, Dosya veya Web taranarak anında öğrenme' },
        { icon: <IntegrationIcon />, title: 'Kurumsal Entegrasyonlar', text: 'SharePoint ve bulut sistemleriyle tam uyum' },
        { icon: <AutoAwesomeIcon />, title: 'Sürekli Gelişim', text: 'Kullanım raporları ile mükemmel kullanıcı deneyimi' },
        { icon: <BusinessIcon />, title: 'Esnek Planlar', text: 'Her ölçekteki işletme için uygun seçenek' },
        { icon: <LanguageIcon />, title: 'Yerli ve Güçlü', text: '%100 Türkçe destek ve yerel barındırma seçenekleri' },
        { icon: <SecurityIcon />, title: 'Maksimum Güvenlik', text: 'Verileriniz izole ve şifrelenmiş olarak saklanır' }
    ];

    // Abonelik planları
    const subscriptionPlans = [
        {
            name: 'Starter',
            description: 'Küçük işletmeler için ideal',
            features: ['1 Chatbot', '1.000 Mesaj/Ay', '2 Kullanıcı', 'Temel Analiz']
        },
        {
            name: 'Pro',
            description: 'Büyüyen işletmeler için',
            features: ['5 Chatbot', '10.000 Mesaj/Ay', '10 Kullanıcı', 'Gelişmiş Analiz', 'SharePoint Entegrasyonu']
        },
        {
            name: 'Enterprise',
            description: 'Kurumsal çözümler için',
            features: ['Sınırsız Chatbot', 'Sınırsız Mesaj', 'Sınırsız Kullanıcı', 'Özel Analiz', 'Tüm Entegrasyonlar', 'Özel Destek']
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #0b214bff 0%, #1e3c72 100%)',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Animated Background */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 180, 360],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <SmartToyIcon sx={{ fontSize: 50, mr: 2 }} />
                                    <Typography variant="h1" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                                        Chat-CAT
                                    </Typography>
                                </Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 300 }}>
                                    Yapay Zeka Destekli Chatbot Platformu
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.6 }}>
                                    İşletmelerin kendi markalarına özel yapay zeka destekli chatbotlarını dakikalar içinde oluşturmasını,
                                    eğitmesini ve web sitelerine entegre etmesini sağlayan akıllı sohbet asistanı platformu.
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Paper
                                    elevation={10}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1e3c72', width: 50, height: 50, mr: 2 }}>
                                            <SmartToyIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ color: '#1e3c72', fontWeight: 600 }}>
                                                Chat-CAT Asistan
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Çevrimiçi • Hızlı Yanıt
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    {/* Bot Mesajı */}
                                    <Box sx={{ display: 'flex', mb: 2 }}>
                                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1e3c72' }}>
                                            <SmartToyIcon sx={{ fontSize: 18 }} />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: '12px 12px 12px 4px', mb: 0.5 }}>
                                                <Typography variant="body2">
                                                    Merhaba! Size nasıl yardımcı olabilirim?
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                09:30
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Kullanıcı Mesajı */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Box sx={{ maxWidth: '75%' }}>
                                            <Box sx={{ bgcolor: '#1e3c72', color: 'white', p: 2, borderRadius: '12px 12px 4px 12px', mb: 0.5 }}>
                                                <Typography variant="body2">
                                                    Ürünleriniz hakkında bilgi alabilir miyim?
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mr: 1 }}>
                                                09:31
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Bot Yanıt */}
                                    <Box sx={{ display: 'flex', mb: 2 }}>
                                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1e3c72' }}>
                                            <SmartToyIcon sx={{ fontSize: 18 }} />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: '12px 12px 12px 4px' }}>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    Tabii ki! İşte ürün katalogumuz:
                                                </Typography>
                                                <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                        📦 <strong>Model A</strong> - 1.200 TL<br />
                                                        📦 <strong>Model B</strong> - 2.500 TL<br />
                                                        📦 <strong>Model C</strong> - 4.800 TL
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="caption" sx={{ ml: 1, color: 'success.main', fontWeight: 600 }}>
                                                ✓ Yanıtlandı
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Ürün Galerisi */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Chip label="Platform Ekran Görüntüleri" color="primary" sx={{ mb: 2, fontSize: '1rem', py: 2 }} />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Arayüz Önizlemesi
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Chat-CAT platformunun özelliklerini keşfedin
                        </Typography>
                    </Box>

                    <GalleryCarousel />
                </Container>
            </Box>

            {/* Ana Özellikler */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            label="Ana Özellikler"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Güçlü Özellikler, Kolay Kullanım
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            Chat-CAT ile işletmeniz için özel chatbot çözümleri oluşturun
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {mainFeatures.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: feature.color,
                                                    width: 70,
                                                    height: 70,
                                                    mr: 3
                                                }}
                                            >
                                                {feature.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: feature.color }}>
                                                    {feature.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Alanları */}
            <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<ChatIcon />}
                            label="Kullanım Alanları"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#1e3c72' }}>
                            Her Sektöre Uygun Çözümler
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                            minHeight: '220px',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.main',
                                                width: 64,
                                                height: 64,
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        >
                                            {useCase.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 600,
                                                minHeight: '64px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {useCase.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ flex: 1, lineHeight: 1.6 }}
                                        >
                                            {useCase.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Neden Chat-CAT? */}
            <Box sx={{ py: 8, bgcolor: '#1e3c72', color: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                            Neden Chat-CAT?
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Başarınız için tasarlanmış özellikler
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {whyChatCAT.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            minHeight: '80px',
                                            height: '100%'
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                width: 50,
                                                height: 50,
                                                mr: 2,
                                                flexShrink: 0
                                            }}
                                        >
                                            {item.icon}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.5 }}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default ChatCAT;

